import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

// Order List API
export const getOrderList = createAsyncThunk(
  'admin/getOrderList',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('orders/admin/list', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch order list');
    }
  },
);

// Search Orders by Contact (name, phone, order ID)
export const searchOrdersByContact = createAsyncThunk(
  'admin/searchOrdersByContact',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/search-by-contact', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to search orders');
    }
  },
);

// Partner Orders API
export const getPartnerOrdersList = createAsyncThunk(
  'admin/getPartnerOrdersList',
  async (payload = {}, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('orders/delivery-partner-orders', {
        params: payload,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch partner orders');
    }
  },
);

export const updateOrderStatus = createAsyncThunk(
  'admin/updateOrderStatus',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/admin/accept', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update order status');
    }
  },
);

export const cancelOrder = createAsyncThunk(
  'admin/cancelOrder',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/admin/cancel', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel order');
    }
  },
);

export const readyPickupOrder = createAsyncThunk(
  'admin/readyPickupOrder',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/admin/ready-pickup', payload);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to mark order ready for pickup',
      );
    }
  },
);

export const deliveredOrder = createAsyncThunk(
  'admin/deliveredOrder',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/admin/delivered', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark order as delivered');
    }
  },
);

export const getPartnersList = createAsyncThunk(
  'admin/getPartnersList',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/delivery-partners/partners');
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch partners list');
    }
  },
);

// Get Delivery Dates API
export const getDeliveryDates = createAsyncThunk(
  'admin/getDeliveryDates',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/core/delivery-dates?for_admin=true');
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch delivery dates');
    }
  },
);

// Product Summary API
export const getOrderProductSummary = createAsyncThunk(
  'admin/getOrderProductSummary',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('orders/product-summary', payload);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch order product summary',
      );
    }
  },
);

// Driver Assignments API
export const getDriverAssignments = createAsyncThunk(
  'admin/getDriverAssignments',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/orders/driver-assignments');
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch driver assignments');
    }
  },
);

// Custom Capture Amount API
export const customCaptureAmount = createAsyncThunk(
  'admin/customCaptureAmount',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/custom-capture-amount', payload);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to process custom capture amount',
      );
    }
  },
);

export const getUserList = createAsyncThunk(
  'admin/getUserList',
  async (payload = {}, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(
        '/admin/users/datatable/user-list',
        payload,
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user list');
    }
  },
);

export const getUserRoles = createAsyncThunk(
  'admin/getUserRoles',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/admin/users/roles');
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user roles');
    }
  },
);

export const updateUser = createAsyncThunk(
  'admin/updateUser',
  async (userData, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.put('/admin/users/update', userData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user');
    }
  },
);

export const updateUserStatus = createAsyncThunk(
  'admin/updateUserStatus',
  async (statusData, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.put('/admin/users/status', statusData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update user status');
    }
  },
);

export const deleteUserAccount = createAsyncThunk(
  'admin/deleteUserAccount',
  async (deleteData, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.delete('/admin/users/delete', {
        data: deleteData,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete user account');
    }
  },
);

// Admin Product Management API
export const fetchAdminProductList = createAsyncThunk(
  'admin/fetchAdminProductList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        draw = 1,
        start = 0,
        length = 500,
        searchValue = '',
        orderColumn = 0,
        orderDir = 'desc',
        category_id = null,
        out_of_stock = '',
        status = '',
        start_date = '',
        end_date = '',
      } = params;

      const payload = {
        action: 'product-list',
        draw,
        start,
        length,
        search: {
          value: searchValue,
          regex: 'false',
        },
        order: [{ column: orderColumn, dir: orderDir }],
        category_id: category_id || null,
        out_of_stock,
        status,
        start_date,
        end_date,
      };

      const { data } = await axiosInstance.post('/products/datatable/product-list', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch admin product list');
    }
  },
);

export const updateProductStatus = createAsyncThunk(
  'admin/updateProductStatus',
  async ({ product_id, status }, { rejectWithValue }) => {
    try {
      const payload = {
        action: 'change-product-status',
        product_id,
        status,
      };
      const { data } = await axiosInstance.post('/products/change-product-status', payload);
      return { data, product_id, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update product status');
    }
  },
);

export const createProduct = createAsyncThunk(
  'admin/createProduct',
  async (productData, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/products/add-product', productData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create product');
    }
  },
);

export const updateProduct = createAsyncThunk(
  'admin/updateProduct',
  async ({ productId, productData }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(`/products/update-product`, {
        ...productData,
        product_id: productId,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update product');
    }
  },
);

export const getOrderTypes = createAsyncThunk(
  'admin/getOrderTypes',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/products/get-order-types');
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch order types');
    }
  },
);

export const addOrderType = createAsyncThunk(
  'admin/addOrderType',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/products/add-order-type', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add order type');
    }
  },
);

export const fetchProductDetails = createAsyncThunk(
  'admin/fetchProductDetails',
  async (productId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/products/product-details/${productId}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch product details');
    }
  },
);

// Notifications Management API
export const fetchNotificationsList = createAsyncThunk(
  'admin/fetchNotificationsList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const payload = {
        action: 'notifications-list',
        draw: params.draw || 1,
        start: params.start || 0,
        // length: params.length || 10,
        ...params,
      };
      const { data } = await axiosInstance.post('/notifications/list', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch notifications list');
    }
  },
);

export const updateNotificationStatus = createAsyncThunk(
  'admin/updateNotificationStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(
        `/notifications/${id}/status`,
        { status_value: status },
      );
      return { data, id, status };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update notification status',
      );
    }
  },
);

export const fetchNotificationDetails = createAsyncThunk(
  'admin/fetchNotificationDetails',
  async (notificationId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/notifications/${notificationId}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch notification details',
      );
    }
  },
);

export const updateNotificationDetails = createAsyncThunk(
  'admin/updateNotificationDetails',
  async (notificationData, { rejectWithValue }) => {
    try {
      const { id } = notificationData;
      const { data } = await axiosInstance.put(
        `/notifications/${id}`,
        notificationData,
      );
      return data;
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update notification details',
      );
    }
  },
);

export const fetchOrderStatuses = createAsyncThunk(
  'admin/fetchOrderStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(
        '/notifications/types/default/status',
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch order statuses');
    }
  },
);

export const fetchUserDataWithId = createAsyncThunk(
  'admin/fetchUserDataWithId',
  async (user_id, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/auth/me/${user_id}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch product details');
    }
  },
);

// Order Tracking List API
export const getOrderTrackingList = createAsyncThunk(
  'admin/getOrderTrackingList',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('orders/order-tracking-list', {
        params: payload,
      });
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch order tracking list',
      );
    }
  },
);

// Get Drivers List API
export const getDriversList = createAsyncThunk(
  'admin/getDriversList',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('orders/drivers');
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch drivers list');
    }
  },
);

// Get Delivery Partners List API
export const getDeliveryPartnersList = createAsyncThunk(
  'admin/getDeliveryPartnersList',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('menus/delivery-partners');
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch delivery partners list',
      );
    }
  },
);

// Cut-off Times Management API
export const getCutOffTimes = createAsyncThunk(
  'admin/getCutOffTimes',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('orders/cut-off-times');
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch cut-off times');
    }
  },
);

export const manageCutOffTime = createAsyncThunk(
  'admin/manageCutOffTime',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('orders/cut-off-times', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to manage cut-off time');
    }
  },
);

export const getUnCapturedOrdersList = createAsyncThunk(
  'admin/getPartnersList',
  async (orderType = 'delivery-partner', { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/orders/uncaptured-orders?order_type=${orderType}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch uncaptured orders list',
      );
    }
  },
);

export const getInventoryAlerts = createAsyncThunk(
  'admin/getInventoryAlerts',
  async ({ threshold = 0.8, page = 1, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(
        `/products/inventory-alerts?threshold=${threshold}&page=${page}&limit=${limit}`,
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch inventory alerts',
      );
    }
  },
);

export const customPrintLabel = createAsyncThunk(
  'admin/customPrintLabel',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/custom-print-label', payload);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to Create Custom Print Label',
      );
    }
  },
);

export const fetchCustomPrintLabel = createAsyncThunk(
  'admin/fetchCustomPrintLabel',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/orders/custom-print-label/${payload}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to Create Custom Print Label',
      );
    }
  },
);

export const updateDriverAssignment = createAsyncThunk(
  'admin/updateDriverAssignment',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/driver-assignments', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update driver assignment');
    }
  },
);

export const deleteDriverAssignment = createAsyncThunk(
  'admin/deleteDriverAssignment',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/driver-assignments', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete driver assignment');
    }
  },
);
export const addDriverAssignment = createAsyncThunk(
  'admin/addDriverAssignment',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/driver-assignments', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add driver assignment');
    }
  },
);

// Preference Info API
export const getPreferenceInfo = createAsyncThunk(
  'admin/getPreferenceInfo',
  async (preference_type, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(
        `/products/preference-info?preference_type=${preference_type}`,
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch preference info');
    }
  },
);

// Upload product image
export const uploadProductImage = createAsyncThunk(
  'admin/uploadProductImage',
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/products/product-image-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload product image');
    }
  },
);

// Change Cash Status API
export const changeCashStatus = createAsyncThunk(
  'admin/changeCashStatus',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/change-cash-status', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to change cash status');
    }
  },
);

// Change Delivery Date API
export const changeDeliveryDate = createAsyncThunk(
  'admin/changeDeliveryDate',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/change-delivery-date', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to change delivery date');
    }
  },
);

// Change Delivery Partner API
export const changeDeliveryPartner = createAsyncThunk(
  'admin/changeDeliveryPartner',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/change-delivery-partner', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to change delivery partner');
    }
  },
);

export const fetchPrintOrderInfo = createAsyncThunk(
  'admin/fetchPrintOrderInfo',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/print-multi-order-info', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch print order info');
    }
  },
);

// Update Order Items API
export const updateOrderItems = createAsyncThunk(
  'admin/updateOrderItems',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/admin/update-order-items', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update order items');
    }
  },
);

// Update Order assign drivers API
export const assignDrivers = createAsyncThunk(
  'admin/assignDrivers',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/admin/assign-drivers', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to assign Drivers,');
    }
  },
);

export const fetchOrderTrackingDetails = createAsyncThunk(
  'admin/fetchOrderTrackingDetails',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/orders/tracking-info', {
        params: params,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch print order info');
    }
  },
);

export const fetchAdminUserDetails = createAsyncThunk(
  'admin/fetchAdminUserDetails',
  async (user_id, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/admin/users/${user_id}/details`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user details');
    }
  },
);

// Get Order Validation List
export const getOrderValidationList = createAsyncThunk(
  'admin/getOrderValidationList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, status = '' } = params;
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', limit);
      if (status !== '') queryParams.append('status', status);

      const { data } = await axiosInstance.get(
        `/orders/order-validation-list?${queryParams.toString()}`,
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch order validation list',
      );
    }
  },
);

// Update Order Validation Status
export const updateOrderValidationStatus = createAsyncThunk(
  'admin/updateOrderValidationStatus',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.put('/orders/order-validation-status', payload);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update order validation status',
      );
    }
  },
);

// Get Zelle Payments List
export const getZellePaymentsList = createAsyncThunk(
  'admin/getZellePaymentsList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/orders/zelle-payments/list', {
        params: params,
      });
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch zelle payments list',
      );
    }
  },
);

// Get Reviews List API
export const getReviewsList = createAsyncThunk(
  'admin/getReviewsList',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/admin/reviews/list', payload);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch reviews list',
      );
    }
  },
);

// Cron Management APIs
export const getCronSchedules = createAsyncThunk(
  'admin/getCronSchedules',
  async (params = { page: 1, limit: 10 }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/admin/crons', {
        params: params,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch cron schedules');
    }
  },
);

export const getCronDetails = createAsyncThunk(
  'admin/getCronDetails',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/admin/crons/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch cron details');
    }
  },
);

export const createCronSchedule = createAsyncThunk(
  'admin/createCronSchedule',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/admin/crons', payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create cron schedule');
    }
  },
);

export const updateCronSchedule = createAsyncThunk(
  'admin/updateCronSchedule',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.put(`/admin/crons/${id}`, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update cron schedule');
    }
  },
);

export const deleteCronSchedule = createAsyncThunk(
  'admin/deleteCronSchedule',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.delete(`/admin/crons/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete cron schedule');
    }
  },
);

export const triggerCronJob = createAsyncThunk(
  'admin/triggerCronJob',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(`/admin/crons/${id}/trigger`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to trigger cron job');
    }
  },
);

// Map Orders to Zelle Payment
export const mapOrdersToZellePayment = createAsyncThunk(
  'admin/mapOrdersToZellePayment',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/zelle-payment/mapping-orders', payload);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to map orders to Zelle payment',
      );
    }
  },
);

// Unmap Order from Zelle Payment
export const unmapOrderFromZellePayment = createAsyncThunk(
  'admin/unmapOrderFromZellePayment',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/zelle-payment/unmap-order', payload);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to unmap order from Zelle payment',
      );
    }
  },
);

// Assign Partner to Zelle Payment
export const assignPartnerToZellePayment = createAsyncThunk(
  'admin/assignPartnerToZellePayment',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/zelle-payment/assign-partner', payload);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to assign partner to Zelle payment',
      );
    }
  },
);

// Update Zelle Payment Phone Number
export const updateZellePaymentPhone = createAsyncThunk(
  'admin/updateZellePaymentPhone',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/orders/zelle-payment/update-phone', payload);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update phone number',
      );
    }
  },
);

export const updateProductAttributes = createAsyncThunk(
  'admin/updateProductAttributes',
  async ({ product_id, out_of_stock }, { rejectWithValue }) => {
    try {
      const payload = {
        product_id,
        out_of_stock,
      };
      const { data } = await axiosInstance.post('/products/update-product-attributes', payload);
      return { data, product_id, out_of_stock };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update product attributes',
      );
    }
  },
);

export const updateProductQuantity = createAsyncThunk(
  'admin/updateProductQuantity',
  async ({ product_id, quantity }, { rejectWithValue }) => {
    try {
      const payload = {
        product_id,
        quantity,
      };
      const { data } = await axiosInstance.post('/products/update-product-attributes', payload);
      return { data, product_id, quantity };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update product attributes',
      );
    }
  },
);

// Admin Subscription Users List
export const updateQurbaniBehalfName = createAsyncThunk(
  'admin/updateQurbaniBehalfName',
  async ({ orderId, behalfName }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/qurbani/admin/orders/behalf', {
        action: 'update-behalf-name',
        order_id: orderId,
        behalf_name: behalfName,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update behalf name');
    }
  },
);

export const updateQurbaniCharity = createAsyncThunk(
  'admin/updateQurbaniCharity',
  async ({ orderId, donateCharity }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/qurbani/admin/orders/behalf', {
        action: 'update-charity-option',
        order_id: orderId,
        donate_charity: donateCharity,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update charity option');
    }
  },
);

export const processAdminRefund = createAsyncThunk(
  'admin/processRefund',
  async ({ paymentId, amount, paymentMethod, description }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/admin/users/process-refund', {
        payment_id: paymentId,
        amount,
        payment_method: paymentMethod,
        reward_type: 'order_refund',
        description,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to process refund');
    }
  },
);

export const addRefundRewards = createAsyncThunk(
  'admin/addRefundRewards',
  async ({ userId, rewardType, paymentId, amount, description }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/admin/users/add-refund-rewards', {
        user_id: userId,
        reward_type: rewardType,
        payment_id: paymentId,
        amount,
        description,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add refund rewards');
    }
  },
);

export const getAdminSubscriptionUsers = createAsyncThunk(
  'admin/getAdminSubscriptionUsers',
  async (payload = {}, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/admin/subscriptions/users', {
        params: payload,
      });
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch subscription users',
      );
    }
  },
);

// ─── Driver Work Timeline Admin APIs ─────────────────────────────────────────

// Fetch driver timeline (stats + works) for a date range
export const fetchDriverTimeline = createAsyncThunk(
  'admin/fetchDriverTimeline',
  async ({ driverId, startDate, endDate, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(
        `/driver/admin/${driverId}/work-timeline`,
        { params: { start_date: startDate, end_date: endDate, page, limit } },
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch driver timeline',
      );
    }
  },
);

// Admin override a work entry (correct times / status)
export const adminCorrectWork = createAsyncThunk(
  'admin/adminCorrectWork',
  async ({ driverId, workId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.put(
        `/driver/admin/${driverId}/work-timeline/${workId}`,
        payload,
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to correct work entry',
      );
    }
  },
);

// Update driver hourly rate
export const adminUpdateDriverRate = createAsyncThunk(
  'admin/adminUpdateDriverRate',
  async ({ driverId, rate, currency }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.put(
        `/driver/admin/${driverId}/rate`,
        { driver: { rate, currency } },
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update driver rate',
      );
    }
  },
);

// Settle (mark as paid) selected work log entries
export const adminSettlePayments = createAsyncThunk(
  'admin/adminSettlePayments',
  async ({ driverId, workIds, status }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(
        '/driver/admin/work-timeline/settle',
        {
          driver_id: driverId,
          work_ids: workIds,
          status,
        },
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to settle payments',
      );
    }
  },
);


// Admin manually add a single work timeline entry for a driver
export const adminAddWorkEntry = createAsyncThunk(
  'admin/adminAddWorkEntry',
  async ({ driverId, entryData }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(
        `/driver/admin/${driverId}/work-timeline/add`,
        entryData,
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to add work entry',
      );
    }
  },
);

// Admin bulk-add multiple work timeline entries for a driver
export const adminBulkAddWorkEntries = createAsyncThunk(
  'admin/adminBulkAddWorkEntries',
  async ({ driverId, entries }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(
        '/driver/admin/work-timeline/bulk-add',
        { driver_id: driverId, entries },
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to bulk add work entries',
      );
    }
  },
);

// Admin bulk-add multiple work task logs for a driver's work record
export const adminBulkAddWorkLogs = createAsyncThunk(
  'admin/adminBulkAddWorkLogs',
  async ({ driverId, logs }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(
        `/driver/admin/${driverId}/work-timeline/logs/bulk`,
        logs,
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to bulk add work logs',
      );
    }
  },
);

// Admin delete any work entry for a driver
export const adminDeleteWork = createAsyncThunk(
  'admin/adminDeleteWork',
  async ({ driverId, workId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.delete(
        `/driver/admin/${driverId}/work-timeline/${workId}`,
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete work entry',
      );
    }
  },
);

// Admin delete a driver's work log
export const adminDeleteWorkLog = createAsyncThunk(
  'admin/adminDeleteWorkLog',
  async ({ driverId, logId }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.delete(
        `/driver/admin/${driverId}/work-timeline/logs/${logId}`,
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete work log',
      );
    }
  },
);

// Admin update a driver's work log
export const adminUpdateWorkLog = createAsyncThunk(
  'admin/adminUpdateWorkLog',
  async ({ driverId, logId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.put(
        `/driver/admin/${driverId}/work-timeline/logs/${logId}`,
        payload
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update work log',
      );
    }
  },
);

// Get Refund List API
export const getRefundList = createAsyncThunk(
  'admin/getRefundList',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post('/admin/users/datatable/refund-list', payload);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch refund list',
      );
    }
  },
);

export const getOrderListRewards = createAsyncThunk(
  'admin/getOrderListRewards',
  async (order_id, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/orders/get-order-info', {
        params: { order_id },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch order info');
    }
  },
);